import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, X } from "lucide-react";

const input = "w-full bg-transparent border hairline px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]";

export function ChangePasswordForm({ forced = false, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div>
        <label className="label-eyebrow block mb-2">{forced ? "Temporary password" : "Current password"}</label>
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
               className={input} data-testid="cp-current" autoComplete="current-password" />
      </div>
      <div>
        <label className="label-eyebrow block mb-2">New password</label>
        <input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
               className={input} data-testid="cp-new" autoComplete="new-password" />
      </div>
      <div>
        <label className="label-eyebrow block mb-2">Confirm new password</label>
        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
               className={input} data-testid="cp-confirm" autoComplete="new-password" />
      </div>
      <button disabled={busy} type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3 font-semibold rounded-sm transition-colors disabled:opacity-60"
              data-testid="cp-submit">
        <KeyRound size={14} /> {busy ? "Saving…" : "Set new password"}
      </button>
    </form>
  );
}

export function ChangePasswordModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="change-password-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[color:var(--brand-bg)] border hairline w-full max-w-md p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 hover:text-black" data-testid="cp-close">
          <X size={18} />
        </button>
        <div className="label-eyebrow mb-2">Account security</div>
        <h2 className="font-display text-2xl mb-6">Change password</h2>
        <ChangePasswordForm onDone={onClose} />
      </div>
    </div>
  );
}

export function ForcedPasswordGate({ children }) {
  const { user, refresh } = useAuth();
  if (!user || !user.must_change_password) return children;
  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="forced-password-change">
      <div className="w-full max-w-md border hairline bg-white p-8">
        <div className="label-eyebrow mb-2 text-[var(--brand-primary)]">Action required</div>
        <h1 className="font-display text-3xl mb-3">Set your new password</h1>
        <p className="text-sm text-neutral-500 mb-8">
          You signed in with a temporary password. Choose a new one to continue.
        </p>
        <ChangePasswordForm forced onDone={refresh} />
      </div>
    </div>
  );
}
